import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateCurrentUser } from '@services/settings';
import { toast } from 'react-toastify';
import { User } from '@models/user.model';
import { USER_CURRENT_QUERY_KEY } from './useCurrentUser';
import { useTranslation } from '@hooks/useTranslation';

export interface UseUpdateCurrentUserOptions {
  /**
   * Sprint 27, story-139 follow-up. The default success/error toasts here are hardcoded
   * English, which is fine for the name/avatar forms (they have no locale-aware copy of their
   * own) but wrong for callers — like LanguageSwitcher — that already show their own localized
   * toast. Suppress the hook's toast in that case rather than deleting it outright, so the
   * default callers keep their existing behaviour unchanged.
   */
  suppressSuccessToast?: boolean;
  suppressErrorToast?: boolean;
}

export const useUpdateCurrentUser = (options: UseUpdateCurrentUserOptions = {}) => {
  const tActionMessages = useTranslation('actionMessages');
  const { suppressSuccessToast, suppressErrorToast } = options;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (user: Partial<User>) => updateCurrentUser(user),
    onSuccess: async () => {
      if (!suppressSuccessToast) {
        toast.success(tActionMessages.profileUpdatedSuccessfully);
      }
      await queryClient.invalidateQueries({ queryKey: USER_CURRENT_QUERY_KEY });
    },
    onError: (error: any) => {
      if (!suppressErrorToast) {
        const message = error?.response?.errors?.[0]?.message || tActionMessages.requestError;
        toast.error(message);
      }
    },
  });
};

export default useUpdateCurrentUser;
